import type { HrDocument } from '@/types/document.types';
import type { SubmissionDetail } from '@/types/submission.types';

export type WarningSeverity = 'error' | 'warning';
export type WarningType = 'expired' | 'expiring_soon' | 'missing_document' | 'document_flagged' | 'data_mismatch';

export interface SubmissionWarning {
  type: WarningType;
  severity: WarningSeverity;
  field: string;
  message: string;
  documentId?: string;
  details?: {
    applicationValue?: string;
    documentValue?: string;
  };
}

type TFunction = (key: string, params?: Record<string, unknown>) => string;

const EXPIRING_SOON_DAYS = 30;

function daysBetween(from: Date, to: Date): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.floor((to.getTime() - from.getTime()) / msPerDay);
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0] ?? '';
}

function checkDateExpiration(
  dateStr: string | undefined,
  fieldKey: string,
  t: TFunction,
): SubmissionWarning | undefined {
  if (!dateStr) {
    return undefined;
  }

  const expirationDate = new Date(dateStr);
  if (isNaN(expirationDate.getTime())) {
    return undefined;
  }

  const now = new Date();
  const days = daysBetween(now, expirationDate);
  const fieldLabel = t(`warnings.fields.${fieldKey}`);
  const formattedDate = formatDate(expirationDate);

  if (days < 0) {
    return {
      type: 'expired',
      severity: 'error',
      field: fieldKey,
      message: t('warnings.expired', { field: fieldLabel, date: formattedDate }),
    };
  }

  if (days <= EXPIRING_SOON_DAYS) {
    return {
      type: 'expiring_soon',
      severity: 'warning',
      field: fieldKey,
      message: t('warnings.expiringSoon', { field: fieldLabel, date: formattedDate, days }),
    };
  }

  return undefined;
}

function computeDateWarnings(detail: SubmissionDetail, t: TFunction): SubmissionWarning[] {
  const warnings: SubmissionWarning[] = [];

  // Florida licenses
  const fl = detail.stepC.florida;
  if (fl) {
    if (fl.hasClassD) {
      const w = checkDateExpiration(fl.classDExpiration, 'classDExpiration', t);
      if (w) {
        warnings.push(w);
      }
    }
    if (fl.hasClassG) {
      const w = checkDateExpiration(fl.classGExpiration, 'classGExpiration', t);
      if (w) {
        warnings.push(w);
      }
    }
  }

  // New York licenses
  const ny = detail.stepC.newYork;
  if (ny) {
    if (ny.hasGuardCard) {
      const w = checkDateExpiration(ny.guardCardExpiration, 'guardCardExpiration', t);
      if (w) {
        warnings.push(w);
      }
    }
    if (ny.hasFirearmCreds) {
      const w = checkDateExpiration(ny.firearmCredsExpiration, 'firearmCredsExpiration', t);
      if (w) {
        warnings.push(w);
      }
    }
  }

  // Certifications
  const certs = detail.stepC.certifications;
  if (certs.hasCprAed) {
    const w = checkDateExpiration(certs.cprAedExpiration, 'cprAedExpiration', t);
    if (w) {
      warnings.push(w);
    }
  }
  if (certs.hasTwic) {
    const w = checkDateExpiration(certs.twicExpiration, 'twicExpiration', t);
    if (w) {
      warnings.push(w);
    }
  }

  return warnings;
}

function computeDocumentFlagWarnings(documents: HrDocument[], t: TFunction): SubmissionWarning[] {
  const warnings: SubmissionWarning[] = [];

  for (const doc of documents) {
    if (doc.status === 'rejected') {
      warnings.push({
        type: 'document_flagged',
        severity: 'error',
        field: doc.type,
        message: t('warnings.documentRejected', { type: doc.type, filename: doc.filename }),
        documentId: doc.documentId,
      });
    }

    if (doc.flags?.expired) {
      warnings.push({
        type: 'document_flagged',
        severity: 'error',
        field: doc.type,
        message: t('warnings.documentExpired', { filename: doc.filename }),
        documentId: doc.documentId,
      });
    }

    if (doc.flags?.nameMismatch) {
      warnings.push({
        type: 'document_flagged',
        severity: 'warning',
        field: doc.type,
        message: t('warnings.documentNameMismatch', { filename: doc.filename }),
        documentId: doc.documentId,
      });
    }

    if (doc.flags?.illegible) {
      warnings.push({
        type: 'document_flagged',
        severity: 'warning',
        field: doc.type,
        message: t('warnings.documentIllegible', { filename: doc.filename }),
        documentId: doc.documentId,
      });
    }
  }

  return warnings;
}

function normalizeStr(value: string): string {
  return value.replace(/[\s-]/g, '').toLowerCase();
}

function parseDateLoose(value: string): Date | undefined {
  const parsed = new Date(value);
  if (!isNaN(parsed.getTime())) {
    return parsed;
  }
  return undefined;
}

function datesMatch(a: Date, b: Date): boolean {
  return formatDate(a) === formatDate(b);
}

function collectApplicationLicenseNumbers(detail: SubmissionDetail): string[] {
  const numbers: string[] = [];
  const fl = detail.stepC.florida;
  if (fl) {
    if (fl.hasClassD && fl.classDNumber) {
      numbers.push(fl.classDNumber);
    }
    if (fl.hasClassG && fl.classGNumber) {
      numbers.push(fl.classGNumber);
    }
  }
  const ny = detail.stepC.newYork;
  if (ny) {
    if (ny.hasGuardCard && ny.guardCardNumber) {
      numbers.push(ny.guardCardNumber);
    }
    if (ny.hasFirearmCreds && ny.firearmCredsNumber) {
      numbers.push(ny.firearmCredsNumber);
    }
  }
  return numbers;
}

function collectApplicationDates(detail: SubmissionDetail): string[] {
  const dates: string[] = [];
  const fl = detail.stepC.florida;
  if (fl) {
    if (fl.hasClassD && fl.classDExpiration) {
      dates.push(fl.classDExpiration);
    }
    if (fl.hasClassG && fl.classGExpiration) {
      dates.push(fl.classGExpiration);
    }
  }
  const ny = detail.stepC.newYork;
  if (ny) {
    if (ny.hasGuardCard && ny.guardCardExpiration) {
      dates.push(ny.guardCardExpiration);
    }
    if (ny.hasFirearmCreds && ny.firearmCredsExpiration) {
      dates.push(ny.firearmCredsExpiration);
    }
  }
  const certs = detail.stepC.certifications;
  if (certs.hasCprAed && certs.cprAedExpiration) {
    dates.push(certs.cprAedExpiration);
  }
  if (certs.hasTwic && certs.twicExpiration) {
    dates.push(certs.twicExpiration);
  }
  return dates;
}

function computeCrossValidationWarnings(
  detail: SubmissionDetail,
  documents: HrDocument[],
  t: TFunction,
): SubmissionWarning[] {
  const warnings: SubmissionWarning[] = [];
  const applicationName = `${detail.stepA.firstName} ${detail.stepA.lastName}`.trim().toLowerCase();
  const applicationNumbers = collectApplicationLicenseNumbers(detail);
  const applicationDates = collectApplicationDates(detail);

  for (const doc of documents) {
    if (!doc.extracted || doc.status === 'rejected') {
      continue;
    }

    const extracted = doc.extracted;

    // Name comparison
    if (extracted.name) {
      const extractedName = extracted.name.trim().toLowerCase();
      if (extractedName !== applicationName) {
        warnings.push({
          type: 'data_mismatch',
          severity: 'warning',
          field: 'name',
          message: t('warnings.crossValidation.name', {
            applicationValue: `${detail.stepA.firstName} ${detail.stepA.lastName}`.trim(),
            documentValue: extracted.name.trim(),
            filename: doc.filename,
          }),
          documentId: doc.documentId,
          details: {
            applicationValue: `${detail.stepA.firstName} ${detail.stepA.lastName}`.trim(),
            documentValue: extracted.name.trim(),
          },
        });
      }
    }

    // Document number comparison
    const extractedNumber = extracted.documentNumber ?? extracted.number;
    if (extractedNumber && applicationNumbers.length > 0) {
      const normalizedExtracted = normalizeStr(extractedNumber);
      const hasMatch = applicationNumbers.some((num) => normalizeStr(num) === normalizedExtracted);
      if (!hasMatch) {
        warnings.push({
          type: 'data_mismatch',
          severity: 'warning',
          field: 'documentNumber',
          message: t('warnings.crossValidation.number', {
            applicationValue: applicationNumbers.join(', '),
            documentValue: extractedNumber,
            filename: doc.filename,
            fieldName: 'documentNumber',
          }),
          documentId: doc.documentId,
          details: {
            applicationValue: applicationNumbers.join(', '),
            documentValue: extractedNumber,
          },
        });
      }
    }

    // Expiration date comparison
    const extractedExpStr = extracted.expirationDate ?? extracted.expiresOn;
    if (extractedExpStr && applicationDates.length > 0) {
      const extractedExp = parseDateLoose(extractedExpStr);
      if (extractedExp) {
        const hasMatch = applicationDates.some((dateStr) => {
          const appDate = parseDateLoose(dateStr);
          return appDate ? datesMatch(appDate, extractedExp) : false;
        });
        if (!hasMatch) {
          warnings.push({
            type: 'data_mismatch',
            severity: 'warning',
            field: 'expirationDate',
            message: t('warnings.crossValidation.expiration', {
              applicationValue: applicationDates.join(', '),
              documentValue: extractedExpStr,
              filename: doc.filename,
              fieldName: 'expirationDate',
            }),
            documentId: doc.documentId,
            details: {
              applicationValue: applicationDates.join(', '),
              documentValue: extractedExpStr,
            },
          });
        }
      }
    }
  }

  return warnings;
}

function severityOrder(severity: WarningSeverity): number {
  return severity === 'error' ? 0 : 1;
}

export function computeSubmissionWarnings(
  detail: SubmissionDetail,
  documents: HrDocument[],
  t: TFunction,
): SubmissionWarning[] {
  const dateWarnings = computeDateWarnings(detail, t);
  const flagWarnings = computeDocumentFlagWarnings(documents, t);
  const crossWarnings = computeCrossValidationWarnings(detail, documents, t);

  const allWarnings = [...dateWarnings, ...flagWarnings, ...crossWarnings];

  allWarnings.sort((a, b) => severityOrder(a.severity) - severityOrder(b.severity));

  return allWarnings;
}
